import { useMemo, useState } from 'react';
import { Spin, Modal, Typography } from 'antd';
import dayjs from 'dayjs';
import NoDataState from '../../../RnRCommonComponent/noDataState/NoDataState.jsx';
import {
	addCommas,
	getRecognizerName,
	isAutomatedRecognizer,
} from '../../../rewardsAndRecognitionHelper/utils.js';
import { useSelector } from 'react-redux';
import iconReceived from 'src/_shared/assets/IconReceived.svg';
import iconSend from 'src/_shared/assets/IconSend.svg';
import CoreBadge from '../../../RnRCommonComponent/CoreBadge/CoreBadge.jsx';
import { REVERSE_MAPING_BADGE_TYPE } from '../../../RnRCommonComponent/CoreBadge/rewardsConstants.js';

const { Text } = Typography;

const RecentActivity = ({ badgeList, assignmentsSent, isLoading, error }) => {
	const currentUser = useSelector((state) => state?.user?.currentUser);
	const [selectedActivity, setSelectedActivity] = useState(null);
	const [isModalVisible, setIsModalVisible] = useState(false);

	const handleActivityClick = (activity) => {
		setSelectedActivity(activity);
		setIsModalVisible(true);
	};

	const handleCloseModal = () => {
		setIsModalVisible(false);
		setSelectedActivity(null);
	};

	const activityDetails = useMemo(() => {
		if (!assignmentsSent || !badgeList) {
			return <NoDataState title="No recent activity." />;
		}

		const receivedAndSent = [...badgeList, ...assignmentsSent];

		if (receivedAndSent.length === 0) {
			return <NoDataState title="No recent activity." />;
		}

		const sortedActivities = [...receivedAndSent]
			.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
			.map((obj) => {
				let activityType;

				if (
					obj?.awardedBy !== currentUser?.id &&
					(obj?.badgeType === 'Anniversary' ||
						isAutomatedRecognizer(obj?.awardedBy))
				) {
					activityType = 'ServiceAwardReceived';
				} else if (obj?.awardedBy === currentUser?.id && !obj?.badgeId) {
					activityType = 'ShoutOutSent';
				} else if (
					obj?.awardedBy === currentUser?.id &&
					obj?.badgeId &&
					obj?.content?.message
				) {
					activityType = 'ShoutOutRewardSent';
				} else if (
					obj?.awardedBy === currentUser?.id &&
					obj?.badgeId &&
					!obj?.content?.message
				) {
					activityType = 'RewardSent';
				} else if (obj?.awardedBy !== currentUser?.id && !obj?.badgeId) {
					activityType = 'ShoutOutReceived';
				} else if (
					obj?.awardedBy !== currentUser?.id &&
					obj?.badgeId &&
					obj?.content?.message
				) {
					activityType = 'ShoutOutRewardReceived';
				} else if (
					obj?.awardedBy !== currentUser?.id &&
					obj?.badgeId &&
					!obj?.content?.message
				) {
					activityType = 'RewardReceived';
				} else if (
					obj?.awardedBy !== currentUser?.id &&
					obj?.badgeType === 'Nominated'
				) {
					activityType = 'NominationAwardReceived';
				} else {
					activityType =
						obj?.awardedBy === currentUser?.id
							? 'RewardSent'
							: 'RewardReceived';
				}

				return { ...obj, activityType };
			});

		return sortedActivities
			.map((activity) => {
				const formattedDate = dayjs(activity.createdAt).format(
					'M/D/YYYY h:mm A'
				);

				const targetName = activity?.activityType?.includes('Received')
					? getRecognizerName(
							activity?.awardedBy,
							activity?.awardedByUser,
							currentUser?.company?.name
						)
					: `${activity?.user?.firstName || activity?.accountClaim?.firstName} ${activity?.user?.lastName || activity?.accountClaim?.lastName}`;

				const badgeName = activity?.badge?.name || 'Badge';
				const pts = activity?.points || null;

				const recentActivityTemplateData = {
					ShoutOutSent: `You sent ${targetName} a shout out!`,
					ShoutOutRewardSent: `You sent ${targetName} the ${badgeName} recognition${pts ? `, a shout out, and they received ${addCommas(pts)} points` : ' and a shout out'}!`,
					RewardSent: `You sent ${targetName} the ${badgeName} recognition${pts ? ` and they received ${addCommas(pts)} points` : ''}!`,
					ShoutOutReceived: `${targetName} sent you a shout out!`,
					ShoutOutRewardReceived: `${targetName} sent you the ${badgeName} recognition${pts ? `, a shout out, and you received ${addCommas(pts)} points` : ' and a shout out'}!`,
					RewardReceived: `${targetName} sent you the ${badgeName} recognition${pts ? ` and you received ${addCommas(pts)} points` : ''}!`,
					NominationAwardReceived: `You have been awarded the ${badgeName} recognition${pts ? ` and received ${addCommas(pts)} points` : ''}!`,
					ServiceAwardReceived: `You have earned the ${badgeName} recognition${pts ? ` and received ${addCommas(pts)} points` : ''}!`,
				};

				const activityDetailMessage =
					recentActivityTemplateData[activity.activityType];

				const hasClickableContent =
					activity?.badge ||
					activity?.content?.message ||
					activity?.content?.gifURL;

				const badgeTypeLookup = activity?.badge?.badgeTypeId
					? REVERSE_MAPING_BADGE_TYPE[activity.badge.badgeTypeId]
					: activity?.badgeType;

				return {
					formattedDate,
					activityDetailMessage,
					iconUrl: activity?.iconUrl,
					activityType: activity.activityType,
					isClickable: !!hasClickableContent,
					badge: activity?.badge,
					badgeType: badgeTypeLookup,
					content: activity?.content,
					points: activity?.points,
					awardedByUser: activity?.awardedByUser,
					targetName,
					badgeName,
					rawActivity: activity,
				};
			})
			.filter(Boolean);
	}, [assignmentsSent, badgeList, currentUser?.id]);

	return (
		<div className="card-scroll">
			<div className="d-card">
				<div className="recent-activity">
					<h4 className="recent-activity-title"> My Recent Activity</h4>
					{isLoading ? (
						<div className="d-flex align-items-center justify-content-center w-100 h-100">
							<Spin />
						</div>
					) : (
						<div className="activity-detail">
							{activityDetails?.length ? (
								activityDetails?.map((activity, index) => {
									const {
										formattedDate,
										activityDetailMessage,
										activityType,
										isClickable,
									} = activity;
									return (
										<div
											key={index}
											className="about-activity-detail"
											onClick={() =>
												isClickable && handleActivityClick(activity)
											}
											style={{
												cursor: isClickable ? 'pointer' : 'default',
											}}
										>
											<img
												className="activity-img"
												src={
													activityType?.includes('Received')
														? iconReceived
														: iconSend
												}
												alt="img"
												style={{
													height: 20,
													width: 20,
												}}
											/>
											<span className="activity-info">
												{activityDetailMessage}
												<p className="total-earned">{formattedDate}</p>
											</span>
										</div>
									);
								})
							) : (
								<NoDataState title="No recent activity." />
							)}
						</div>
					)}
				</div>
			</div>
			<Modal
				open={isModalVisible}
				onCancel={handleCloseModal}
				footer={null}
				width={600}
				destroyOnClose
			>
				{selectedActivity && (
					<div className="physical-reward-modal" style={{ padding: '20px 0' }}>
						<div className="row w-100">
							{selectedActivity.badge && (
								<div
									className="col-sm-5 col-12"
									style={{ display: 'flex', justifyContent: 'center' }}
								>
									<div
										style={{
											height: 'auto',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											marginBottom: '20px',
										}}
									>
										<CoreBadge
											badgeType={selectedActivity.badgeType}
											badgeIcon={selectedActivity.badge?.iconInfo?.code}
											label={selectedActivity.badge?.name}
											color={selectedActivity.badge?.color}
											gradientColor={selectedActivity.badge?.gradientColor}
											shape={selectedActivity.badge?.shape}
											badgeRotation={selectedActivity.badge?.badgeRotation}
											customImageSignedUrl={selectedActivity.badge?.customImageSignedUrl}
											labelColor={selectedActivity.badge?.labelColor}
											customIconSignedUrl={selectedActivity.badge?.customIconSignedUrl}
										/>
									</div>
								</div>
							)}
							<div
								className={
									selectedActivity.badge ? 'col-sm-7 col-12' : 'col-12'
								}
							>
								<div className="physical-reward-details">
									<h4
										className="physical-reward-text"
										style={{ marginBottom: '16px' }}
									>
										{selectedActivity.badgeName}
									</h4>
									<div style={{ marginBottom: '16px' }}>
										<Text
											style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}
										>
											{selectedActivity.activityDetailMessage}
										</Text>
									</div>
									<div style={{ marginBottom: '16px' }}>
										<span
											style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)' }}
										>
											{selectedActivity.formattedDate}
										</span>
									</div>
									{selectedActivity.content?.message && (
										<>
											<div style={{ marginBottom: '8px' }}>
												<Text
													style={{
														fontSize: '14px',
														fontWeight: 600,
														color: 'rgba(0, 0, 0, 0.85)',
													}}
												>
													You said:
												</Text>
											</div>
											<div
												style={{
													padding: '12px',
													backgroundColor: 'rgba(0, 0, 0, 0.02)',
													borderRadius: '8px',
													marginBottom: '16px',
													border: '1px solid rgba(0, 0, 0, 0.06)',
												}}
											>
												<Text
													style={{
														fontSize: '16px',
														lineHeight: '1.6',
														fontStyle: 'italic',
														display: 'block',
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
													}}
												>
													"{selectedActivity.content.message}"
												</Text>
											</div>
										</>
									)}
									{selectedActivity.content?.gifURL && (
										<div
											style={{
												marginBottom: '16px',
												borderRadius: '8px',
												overflow: 'hidden',
												display: 'flex',
												justifyContent: 'center',
											}}
										>
											<img
												src={selectedActivity.content.gifURL}
												alt="GIF"
												style={{
													maxWidth: '100%',
													height: 'auto',
													borderRadius: '8px',
												}}
											/>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
			</Modal>
		</div>
	);
};

export default RecentActivity;
